const SPOT_HERO_SELECTOR = '.home-hero-spot';
const SPOT_HERO_MEDIA_SELECTOR = '[data-spot-hero-media]';
const SPOT_HERO_PUCK_SELECTOR = '.home-hero-spot__image';

const SPOT_FEATURE_SECTION_SELECTOR = '[data-home-feature-split-id]';
const SPOT_HOME_PRODUCTS_SELECTOR = '.home-products-grid';

const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

function initPuckScroll3D() {
  const hero = document.querySelector(SPOT_HERO_SELECTOR);
  const heroMedia = hero?.querySelector(SPOT_HERO_MEDIA_SELECTOR);
  const puck = heroMedia?.querySelector(SPOT_HERO_PUCK_SELECTOR);

  if (!hero || !heroMedia) return;
  if (!puck) return;
  if (hero.dataset.spotHero3dReady === 'true') return;
  hero.dataset.spotHero3dReady = 'true';

  // Keep hero puck static (no scroll-driven rotation/stretch).
  puck.style.setProperty('--puck-rotate-x', '0deg');
  puck.style.setProperty('--puck-rotate-y', '0deg');
  puck.style.setProperty('--puck-rotate-z', '0deg');
}

function initPuckDrag3D() {
  const hero = document.querySelector(SPOT_HERO_SELECTOR);
  const heroMedia = hero?.querySelector(SPOT_HERO_MEDIA_SELECTOR);
  const puck = heroMedia?.querySelector('[data-spot-puck-draggable]');

  if (!hero || !heroMedia || !(puck instanceof HTMLElement)) return;
  if (hero.dataset.spotHeroDragReady === 'true') return;
  hero.dataset.spotHeroDragReady = 'true';

  let isDragging = false;
  let pointerId = null;
  let startX = 0;
  let startY = 0;
  let rotateX = 0;
  let rotateY = 0;
  let rafId = null;

  const clampX = (value) => clamp(value, -12, 12);
  const clampY = (value) => clamp(value, -26, 26);

  const applyRotation = () => {
    rafId = null;
    puck.style.setProperty('--puck-rotate-x', `${rotateX.toFixed(2)}deg`);
    puck.style.setProperty('--puck-rotate-y', `${rotateY.toFixed(2)}deg`);
  };

  const requestApply = () => {
    if (rafId !== null) return;
    rafId = window.requestAnimationFrame(applyRotation);
  };

  const stopDragging = () => {
    isDragging = false;
    pointerId = null;
    puck.classList.remove('is-dragging');
  };

  puck.addEventListener('pointerdown', (event) => {
    if (event.button !== 0 && event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
    isDragging = true;
    pointerId = event.pointerId;
    startX = event.clientX;
    startY = event.clientY;
    puck.classList.add('is-dragging');
    puck.setPointerCapture(event.pointerId);
  });

  puck.addEventListener('pointermove', (event) => {
    if (!isDragging || pointerId !== event.pointerId) return;

    const deltaX = event.clientX - startX;
    const deltaY = event.clientY - startY;

    rotateY = clampY(deltaX * 0.16);
    rotateX = clampX(deltaY * -0.12);
    requestApply();
  });

  const onPointerEnd = (event) => {
    if (!isDragging || pointerId !== event.pointerId) return;
    stopDragging();
  };

  spot.addEventListener('pointerup', onPointerEnd);
  spot.addEventListener('pointercancel', onPointerEnd);
  spot.addEventListener('lostpointercapture', stopDragging);
}

function initPuckModelActivation() {
  const hero = document.querySelector(SPOT_HERO_SELECTOR);
  const heroMedia = hero?.querySelector('[data-spot-hero-model]');
  const model = heroMedia?.querySelector('[data-spot-puck-model]');
  const poster = heroMedia?.querySelector('[data-spot-puck-poster]');

  if (!hero || !heroMedia || !(model instanceof HTMLElement) || !(poster instanceof HTMLElement)) return;
  if (hero.dataset.spotHeroModelReady === 'true') return;
  hero.dataset.spotHeroModelReady = 'true';

  const activateModel = () => {
    if (heroMedia.classList.contains('is-model-active')) return;
    heroMedia.classList.add('is-model-active');
    model.removeAttribute('aria-hidden');
  };

  // Keep the exact PNG look initially, switch to GLB only on interaction.
  model.addEventListener('pointerdown', activateModel, { passive: true });
  model.addEventListener('touchstart', activateModel, { passive: true });
  model.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      activateModel();
    }
  });
}

function initFeatureSplitInteractions(root) {
  if (root.dataset.spotFeatureReady === 'true') return;
  root.dataset.spotFeatureReady = 'true';

  const readmoreBlocks = root.querySelectorAll('[data-spot-readmore]');
  if (readmoreBlocks.length) {
    readmoreBlocks.forEach((block) => {
      if (block.dataset.spotReadmoreReady === 'true') return;
      block.dataset.spotReadmoreReady = 'true';

      const toggle = block.querySelector('[data-spot-readmore-toggle]');
      const full = block.querySelector('[data-spot-readmore-full]');
      const lessButton = block.querySelector('[data-spot-readmore-less]');

      if (!toggle || !full) return;

      const setHeights = () => {
        if (block.classList.contains('is-open')) {
          full.style.maxHeight = `${full.scrollHeight}px`;
        } else {
          full.style.maxHeight = '0px';
        }
      };

      const open = () => {
        block.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
        setHeights();
      };

      const close = () => {
        block.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        setHeights();
      };

      toggle.addEventListener('click', (event) => {
        if (toggle.tagName === 'A') {
          event.preventDefault();
          event.stopPropagation();
        }

        if (block.classList.contains('is-open')) {
          close();
        } else {
          open();
        }
      });

      if (lessButton) {
        lessButton.addEventListener('click', close);
      }

      window.addEventListener('resize', setHeights, { passive: true });
      setHeights();
    });
  }

  const toggles = root.querySelectorAll('[data-feature-toggle]');
  if (!toggles.length) return;

  toggles.forEach((trigger) => {
    const targetId = trigger.getAttribute('data-target');
    if (!targetId) return;
    const target = document.getElementById(targetId);
    if (!target) return;

    const setButtonLabel = (open) => {
      const openLabel = trigger.getAttribute('data-open-label');
      const closeLabel = trigger.getAttribute('data-close-label');
      if (open && closeLabel) {
        trigger.textContent = closeLabel;
      } else if (!open && openLabel) {
        trigger.textContent = openLabel;
      }
    };

    const testerTarget = target.hasAttribute('data-addiction-tester') ? target : null;
    if (testerTarget) {
      testerTarget.setAttribute('hidden', '');
      trigger.setAttribute('aria-expanded', 'false');
      setButtonLabel(false);
    }

    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      const isOpen = !target.hasAttribute('hidden');
      const nextOpen = !isOpen;

      if (nextOpen) {
        target.removeAttribute('hidden');
      } else {
        target.setAttribute('hidden', '');
      }

      trigger.setAttribute('aria-expanded', String(nextOpen));
      setButtonLabel(nextOpen);
    });

    const tester = testerTarget;
    if (!tester) return;

    const closeButton = tester.querySelector('[data-tester-close]');
    const slider = tester.querySelector('[data-screen-slider]');
    const output = tester.querySelector('[data-screen-output]');
    const result = tester.querySelector('[data-screen-result]');
    const resultText = tester.querySelector('[data-screen-result-text]');
    const resultDelta = tester.querySelector('[data-screen-result-delta]');
    const resultBar = tester.querySelector('[data-screen-bar]');

    if (!slider || !output || !result || !resultText || !resultDelta || !resultBar) return;

    const averageBase = Number(tester.getAttribute('data-average-minutes')) || 240;
    const formatDuration = (minutesTotal) => {
      const safeTotal = Math.max(0, Math.round(minutesTotal));
      const h = Math.floor(safeTotal / 60);
      const m = safeTotal % 60;
      if (h <= 0) return `${m}m`;
      if (m <= 0) return `${h}h`;
      return `${h}h ${m}m`;
    };

    const getTesterTone = (delta, average) => {
      if (delta === 0) {
        return {
          tone: 'hsl(0 0% 22%)',
          track: 'hsl(0 0% 86%)'
        };
      }

      const ratio = clamp(Math.abs(delta) / average, 0, 1.6);
      const intensity = clamp(ratio / 1.2, 0, 1);

      if (delta > 0) {
        const lightness = 62 - (intensity * 34);
        return {
          tone: `hsl(2 78% ${lightness}%)`,
          track: `hsl(2 72% ${88 - (intensity * 12)}%)`
        };
      }

      const lightness = 46 - (intensity * 16);
      return {
        tone: `hsl(145 58% ${lightness}%)`,
        track: `hsl(145 42% ${86 - (intensity * 10)}%)`
      };
    };

    const calculate = () => {
      const totalMinutes = Number(slider.value);
      const h = Math.floor(totalMinutes / 60);
      const m = totalMinutes % 60;
      output.textContent = m === 0 ? `${h}h` : `${h}h ${m}m`;

      const configuredAverage = Math.max(1, averageBase);
      const delta = totalMinutes - configuredAverage;
      const deltaPercent = Math.round((delta / configuredAverage) * 100);
      let resultLabel = "Du liegst genau im Durchschnitt";
      let resultDeltaText = `${deltaPercent > 0 ? '+' : ''}${deltaPercent}% gegenüber dem Durchschnitt`;

      if (delta > 0) {
        resultLabel = "Du liegst über dem Durchschnitt";
        resultDeltaText = `${deltaPercent > 0 ? '+' : ''}${deltaPercent}% gegenüber dem Durchschnitt · mit spot. ca. ${formatDuration(delta)} weniger pro Tag`;
      } else if (delta < 0) {
        resultLabel = "Du liegst unter dem Durchschnitt";
      }

      resultText.textContent = resultLabel;
      resultDelta.textContent = resultDeltaText;

      const tone = getTesterTone(delta, configuredAverage);
      result.style.setProperty('--tester-tone', tone.tone);
      result.style.setProperty('--tester-track', tone.track);

      const fillPercent = clamp((totalMinutes / (configuredAverage * 2)) * 100, 4, 100);
      resultBar.style.width = `${fillPercent}%`;

      result.removeAttribute('hidden');
    };

    closeButton?.addEventListener('click', () => {
      tester.setAttribute('hidden', '');
      result.setAttribute('hidden', '');
      trigger.setAttribute('aria-expanded', 'false');
      setButtonLabel(false);
    });

    slider.addEventListener('input', calculate);
    calculate();
  });
}

function findCartTarget() {
  return (
    document.querySelector('.spot-site-header .header-actions__cart-icon') ||
    document.querySelector('.spot-site-header .action__cart') ||
    document.querySelector('.spot-site-header [href*="/cart"]')
  );
}

async function updateCartBadgeAndPulse(target) {
  const response = await fetch('/cart.js', {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) return;
  const cart = await response.json();
  const itemCount = Number(cart.item_count) || 0;
  const countText = itemCount > 99 ? '99+' : `${itemCount}`;

  const cartIcons = document.querySelectorAll('.header-actions__cart-icon');
  cartIcons.forEach((icon) => {
    icon.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);
  });

  const bubbles = document.querySelectorAll('.cart-bubble');
  bubbles.forEach((bubble) => {
    bubble.classList.toggle('visually-hidden', itemCount <= 0);
    bubble.classList.add('cart-bubble--animating');
    window.setTimeout(() => bubble.classList.remove('cart-bubble--animating'), 240);

    const countNode = bubble.querySelector('.cart-bubble__text-count');
    if (!(countNode instanceof HTMLElement)) return;
    if (itemCount > 0) {
      countNode.textContent = countText;
      countNode.classList.remove('hidden');
    } else {
      countNode.textContent = '';
      countNode.classList.add('hidden');
    }
  });

  const pulseTarget = target?.closest('.header-actions__action') || target;
  if (pulseTarget instanceof HTMLElement) {
    pulseTarget.classList.remove('spot-cart-pulse');
    void pulseTarget.offsetWidth;
    pulseTarget.classList.add('spot-cart-pulse');
    window.setTimeout(() => pulseTarget.classList.remove('spot-cart-pulse'), 260);
  }
}

function animateFlyToCart(sourceImage, destination) {
  if (reduceMotionQuery.matches) return;
  if (!(sourceImage instanceof HTMLImageElement) || !(destination instanceof Element)) return;

  const sourceRect = sourceImage.getBoundingClientRect();
  const destinationRect = destination.getBoundingClientRect();
  if (!sourceRect.width || !sourceRect.height || !destinationRect.width || !destinationRect.height) return;

  const flyer = sourceImage.cloneNode(true);
  if (!(flyer instanceof HTMLImageElement)) return;

  flyer.classList.add('spot-flyer-image');
  flyer.style.position = 'fixed';
  flyer.style.left = `${sourceRect.left}px`;
  flyer.style.top = `${sourceRect.top}px`;
  flyer.style.width = `${sourceRect.width}px`;
  flyer.style.height = `${sourceRect.height}px`;
  flyer.style.borderRadius = '18px';
  flyer.style.pointerEvents = 'none';
  flyer.style.objectFit = 'cover';
  flyer.style.zIndex = '9999';
  flyer.style.transformOrigin = 'center';

  document.body.appendChild(flyer);

  const deltaX = (destinationRect.left + destinationRect.width / 2) - (sourceRect.left + sourceRect.width / 2);
  const deltaY = (destinationRect.top + destinationRect.height / 2) - (sourceRect.top + sourceRect.height / 2);

  const animation = flyer.animate(
    [
      {
        transform: 'translate3d(0, 0, 0) scale(1)',
        opacity: 0.95,
      },
      {
        transform: `translate3d(${deltaX}px, ${deltaY}px, 0) scale(0.18)`,
        opacity: 0.2,
      },
    ],
    {
      duration: 620,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
      fill: 'forwards',
    }
  );

  animation.finished
    .catch(() => null)
    .finally(() => {
      flyer.remove();
    });
}

function initHomeQuickAdd() {
  const productsGrid = document.querySelector(SPOT_HOME_PRODUCTS_SELECTOR);
  if (!productsGrid) return;
  if (productsGrid.dataset.spotQuickAddReady === 'true') return;
  productsGrid.dataset.spotQuickAddReady = 'true';

  productsGrid.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('[data-spot-quickadd-form]')) return;

    event.preventDefault();
    event.stopPropagation();

    const variantInput = form.querySelector('input[name="id"]');
    const quantityInput = form.querySelector('input[name="quantity"]');
    const submitButton = form.querySelector('[data-spot-quickadd-button]');

    const variantId = Number(variantInput?.value);
    const quantity = Number(quantityInput?.value || 1);

    if (!variantId || Number.isNaN(variantId)) return;

    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = true;
    }

    try {
      const cartSections = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
        .map((component) => component.getAttribute('data-section-id'))
        .filter(Boolean);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity > 0 ? quantity : 1,
          sections: cartSections.join(','),
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      const card = form.closest('.spot-card');
      const sourceImage = card?.querySelector('.spot-card__image');
      const cartTarget = findCartTarget();
      animateFlyToCart(sourceImage, cartTarget);

      if (window.SpotCartSync?.syncCartUI) {
        await window.SpotCartSync.syncCartUI({
          source: 'spot-home-quickadd',
          openDrawer: true,
          openDelay: 220,
        });
      } else {
        await updateCartBadgeAndPulse(cartTarget);
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (submitButton instanceof HTMLButtonElement) {
        submitButton.disabled = false;
      }
    }
  });
}

function initHomeProductsCarousel() {
  const grid = document.querySelector(SPOT_HOME_PRODUCTS_SELECTOR);
  if (!grid) return;
  if (grid.dataset.spotHomeCarouselReady === 'true') return;
  grid.dataset.spotHomeCarouselReady = 'true';

  const track = grid.querySelector('[data-home-products-track]');
  const viewport = grid.querySelector('[data-home-products-viewport]');
  const nextButton = grid.querySelector('[data-home-products-arrow="next"]');

  if (!(track instanceof HTMLElement)) return;
  if (!(viewport instanceof HTMLElement)) return;
  if (!(nextButton instanceof HTMLButtonElement)) return;

  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const getItems = () => Array.from(track.querySelectorAll('.home-products-grid__item'));
  let currentIndex = 0;

  const getStep = () => {
    const firstItem = getItems()[0];
    const gap = Number.parseFloat(window.getComputedStyle(track).columnGap || '0') || 0;
    if (!(firstItem instanceof HTMLElement)) {
      return Math.max(viewport.clientWidth * 0.88, 320);
    }

    return Math.max(firstItem.getBoundingClientRect().width + gap, 260);
  };

  const updateState = () => {
    const itemCount = getItems().length;
    const maxIndex = Math.max(0, itemCount - 3);

    if (!desktopQuery.matches || itemCount <= 3) {
      currentIndex = 0;
      track.style.transform = 'translate3d(0, 0, 0)';
      nextButton.hidden = true;
      nextButton.disabled = true;
      return;
    }

    currentIndex = Math.min(currentIndex, maxIndex);
    const offset = currentIndex * getStep();
    track.style.transform = `translate3d(${-offset}px, 0, 0)`;
    nextButton.hidden = false;
    nextButton.disabled = false;
  };

  nextButton.addEventListener('click', () => {
    const itemCount = getItems().length;
    const maxIndex = Math.max(0, itemCount - 3);

    if (maxIndex <= 0) {
      updateState();
      return;
    }

    currentIndex = currentIndex >= maxIndex ? 0 : currentIndex + 1;
    updateState();
  });

  window.addEventListener('resize', updateState, { passive: true });
  if (typeof desktopQuery.addEventListener === 'function') {
    desktopQuery.addEventListener('change', updateState);
  }

  updateState();
}

function initSpotHomeEnhancements() {
  initPuckScroll3D();
  initPuckModelActivation();
  // Keep hero spot. visually static (no drag/scroll distortion).
  document.querySelectorAll(SPOT_FEATURE_SECTION_SELECTOR).forEach(initFeatureSplitInteractions);
  initHomeQuickAdd();
  initHomeProductsCarousel();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSpotHomeEnhancements);
} else {
  initSpotHomeEnhancements();
}

document.addEventListener('shopify:section:load', initSpotHomeEnhancements);
